/**
 * App.web.tsx — Web app entry point.
 *
 * Functionally identical to App.tsx but without react-native-gesture-handler
 * (GestureHandlerRootView is mobile-only; not required on web).
 *
 * Vite resolves .web.tsx before .tsx, so this file is used for the web build.
 */

import React, { useEffect, useState } from 'react';
import { StatusBar, View, StyleSheet, Text, ActivityIndicator } from 'react-native';
import { bootstrapApp } from '@share/app/startup/bootstrap';
import { ErrorBoundary } from '@share/app/providers/ErrorBoundary';
import { TelemetryService } from '@share/telemetry';
import { Colors, Palette, ToastProvider } from '@share/ui';
import { AppNavigator } from '@share/app/navigation/AppNavigator.web';

export default function App() {
  const [ready, setReady] = useState(false);
  const [fatalError, setFatalError] = useState<string | null>(null);

  useEffect(() => {
    bootstrapApp()
      .then(() => {
        TelemetryService.logInfo('App', 'Bootstrap succeeded');
        setReady(true);
      })
      .catch((err: unknown) => {
        TelemetryService.logError('App', 'Bootstrap failed', err);
        // Show app anyway with degraded state — don't block on DB failure
        setFatalError(err instanceof Error ? err.message : String(err));
        setReady(true);
      });
  }, []);

  if (!ready) {
    return (
      <View style={styles.splash}>
        <ActivityIndicator size="large" color={Palette.gray950} />
      </View>
    );
  }

  return (
    <ErrorBoundary>
      {/* GestureHandlerRootView is not needed on web */}
      <View style={{ flex: 1 }}>
        <ToastProvider>
          <StatusBar barStyle="light-content" backgroundColor={Palette.gray950} />
          <AppNavigator />
          {fatalError && (
            <View style={styles.errorBanner}>
              <Text style={styles.errorText}>⚠ Storage error: {fatalError}</Text>
            </View>
          )}
        </ToastProvider>
      </View>
    </ErrorBoundary>
  );
}

const styles = StyleSheet.create({
  splash: {
    flex: 1,
    backgroundColor: Palette.white,
    alignItems: 'center',
    justifyContent: 'center',
  },
  splashText: {
    color: Palette.black,
    fontSize: 48,
    fontWeight: '900',
    fontStyle: 'italic',
    letterSpacing: -2,
  },
  errorBanner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: Colors.danger,
    padding: 8,
  },
  errorText: {
    color: Palette.white,
    fontSize: 12,
    textAlign: 'center',
  },
});
